import React, { useEffect, useState, useRef } from 'react';
import './Spotlight.css';

interface SpotlightProps {
  target?: string;  // CSS selector
  padding?: number;
  borderRadius?: number;
  onClickOutside?: () => void;
}

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function Spotlight({ 
  target, 
  padding = 8,
  borderRadius = 8,
  onClickOutside 
}: SpotlightProps) {
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!target) {
      setTargetRect(null);
      return;
    }

    let rafId: number | null = null;
    let lastRect: TargetRect | null = null;

    // rAF polling: ReactFlow pans/zooms via CSS transforms on a child container
    // and doesn't fire window scroll events, so we can't rely on scroll listeners.
    // Polling getBoundingClientRect every frame is cheap (the target is one DOM
    // node) and keeps the spotlight glued to the element no matter what causes
    // it to move (pan, zoom, animation, layout shift, etc.).
    const tick = () => {
      const element = document.querySelector(target);
      if (element) {
        const r = element.getBoundingClientRect();
        const next: TargetRect = {
          top: r.top - padding,
          left: r.left - padding,
          width: r.width + padding * 2,
          height: r.height + padding * 2,
        };
        // Only setState when something actually changed (avoid re-render churn)
        if (
          !lastRect ||
          lastRect.top !== next.top ||
          lastRect.left !== next.left ||
          lastRect.width !== next.width ||
          lastRect.height !== next.height
        ) {
          lastRect = next;
          setTargetRect(next);
        }
      } else if (lastRect !== null) {
        lastRect = null;
        setTargetRect(null);
      }
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [target, padding]);
  
  const handleOverlayClick = (e: React.MouseEvent) => {
    // Only trigger if clicking on the overlay itself, not the spotlight area
    if (e.target === overlayRef.current && onClickOutside) {
      onClickOutside();
    }
  };
  
  // No target = full overlay with no spotlight
  if (!target) {
    return (
      <div 
        ref={overlayRef}
        className="spotlight-overlay spotlight-overlay--full"
        onClick={handleOverlayClick}
      />
    );
  }
  
  // Target not found yet
  if (!targetRect) {
    return (
      <div 
        ref={overlayRef}
        className="spotlight-overlay spotlight-overlay--full"
        onClick={handleOverlayClick}
      />
    );
  }
  
  // Create spotlight cutout using clip-path
  const clipPath = `
    polygon(
      0% 0%,
      0% 100%,
      ${targetRect.left}px 100%,
      ${targetRect.left}px ${targetRect.top}px,
      ${targetRect.left + targetRect.width}px ${targetRect.top}px,
      ${targetRect.left + targetRect.width}px ${targetRect.top + targetRect.height}px,
      ${targetRect.left}px ${targetRect.top + targetRect.height}px,
      ${targetRect.left}px 100%,
      100% 100%,
      100% 0%
    )
  `;
  
  return (
    <>
      {/* Dark overlay with cutout */}
      <div 
        ref={overlayRef}
        className="spotlight-overlay"
        style={{ clipPath }}
        onClick={handleOverlayClick}
      />
      
      {/* Spotlight border/glow effect */}
      <div 
        className="spotlight-highlight"
        style={{
          top: targetRect.top,
          left: targetRect.left,
          width: targetRect.width,
          height: targetRect.height,
          borderRadius,
        }}
      />
    </>
  );
}
